import React, { useMemo, useState, useRef, useCallback } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Stars, Html, Line } from '@react-three/drei';
import * as THREE from 'three';
import './MemoryConstellation.css';

// ── Color palette (matches existing MemoryGraph) ──
const CATEGORY_COLORS = {
  'experiences': '#7FCDBB',
  'preferences': '#C49A6C',
  'relationships': '#E8A090',
  'memories': '#B0A8D0',
  'thoughts': '#D8BFD8',
  'goals': '#90C695',
  'uncategorized': '#888888',
};

// ── Space configuration (open cylinder you inhabit from inside) ──
const SPACE = {
  HEIGHT: 14,
  INNER_RADIUS: 2,
  OUTER_RADIUS: 7,
  PARTICLE_COUNT: 600,
};

// ── Deterministic hash ──
function hashStr(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function seededRandom(seed) {
  const x = Math.sin(seed * 9301 + 49297) * 49297;
  return x - Math.floor(x);
}

// ── Compute positions: cylindrical open space ──
function computeSpacePositions(memories) {
  if (!memories || !memories.length)
    return { positioned: [], categoryStats: [], connections: [], timeRange: null };

  const catSet = new Set();
  memories.forEach(m => catSet.add(m.category || 'uncategorized'));
  const categories = [...catSet];

  // Each category gets an angular sector
  const catAngle = {};
  categories.forEach((cat, i) => {
    catAngle[cat] = (i / categories.length) * Math.PI * 2;
  });

  const times = memories.map(m => new Date(m.timestamp).getTime());
  const tMin = Math.min(...times);
  const tMax = Math.max(...times);
  const tSpan = tMax - tMin || 1;

  const positioned = memories.map(mem => {
    const cat = mem.category || 'uncategorized';
    const h = hashStr(mem.id);
    const r1 = seededRandom(h);
    const r2 = seededRandom(h + 1);
    const r3 = seededRandom(h + 2);

    // Time → vertical
    const timeNorm = (new Date(mem.timestamp).getTime() - tMin) / tSpan;
    const y = (timeNorm - 0.5) * SPACE.HEIGHT;

    // Category → angular sector with spread
    const sectorAngle = catAngle[cat];
    const spread = (Math.PI * 2 / Math.max(categories.length, 1)) * 0.7;
    const angle = sectorAngle + (r1 - 0.5) * spread;

    // Random radius
    const radius = SPACE.INNER_RADIUS + r2 * (SPACE.OUTER_RADIUS - SPACE.INNER_RADIUS);
    const yOff = (r3 - 0.5) * 0.5;

    const color = CATEGORY_COLORS[cat] || '#888';
    const textLen = mem.text ? mem.text.length : 50;
    const size = 0.006 + Math.min(textLen / 6000, 0.009);

    return {
      ...mem,
      position: [radius * Math.cos(angle), y + yOff, radius * Math.sin(angle)],
      color, size, cat, timeNorm,
    };
  });

  const catCounts = {};
  positioned.forEach(p => { catCounts[p.cat] = (catCounts[p.cat] || 0) + 1; });
  const categoryStats = categories.map(cat => ({
    name: cat, count: catCounts[cat] || 0, color: CATEGORY_COLORS[cat] || '#888',
  }));

  const connections = [];
  const labelMap = new Map();
  positioned.forEach((mem, i) => {
    (Array.isArray(mem.labels) ? mem.labels : []).forEach(label => {
      if (!labelMap.has(label)) labelMap.set(label, []);
      labelMap.get(label).push(i);
    });
  });
  labelMap.forEach(indices => {
    if (indices.length < 2 || indices.length > 25) return;
    for (let a = 0; a < indices.length && connections.length < 80; a++)
      for (let b = a + 1; b < indices.length && connections.length < 80; b++)
        connections.push({
          from: positioned[indices[a]].position,
          to: positioned[indices[b]].position,
          color: positioned[indices[a]].color,
        });
  });

  return { positioned, categoryStats, connections, timeRange: { min: tMin, max: tMax } };
}

// ── Ambient dust particles (sparse, slow, like floating in space) ──
function DustParticles({ count = SPACE.PARTICLE_COUNT }) {
  const pointsRef = useRef();

  const { positions, colors, driftSpeeds } = useMemo(() => {
    const pos = new Float32Array(count * 3);
    const col = new Float32Array(count * 3);
    const drift = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = SPACE.INNER_RADIUS * 0.3 + Math.random() * (SPACE.OUTER_RADIUS * 1.2);
      const y = (Math.random() - 0.5) * SPACE.HEIGHT * 1.1;

      pos[i * 3] = radius * Math.cos(angle);
      pos[i * 3 + 1] = y;
      pos[i * 3 + 2] = radius * Math.sin(angle);

      const hue = 0.55 + Math.random() * 0.15;
      const c = new THREE.Color().setHSL(hue, 0.12 + Math.random() * 0.15, 0.4 + Math.random() * 0.35);
      col[i * 3] = c.r;
      col[i * 3 + 1] = c.g;
      col[i * 3 + 2] = c.b;

      drift[i * 3] = (Math.random() - 0.5) * 0.002;
      drift[i * 3 + 1] = (Math.random() - 0.5) * 0.0015;
      drift[i * 3 + 2] = (Math.random() - 0.5) * 0.002;
    }

    return { positions: pos, colors: col, driftSpeeds: drift };
  }, [count]);

  useFrame(() => {
    if (!pointsRef.current) return;
    const posAttr = pointsRef.current.geometry.attributes.position;
    if (!posAttr) return;

    for (let i = 0; i < count; i++) {
      posAttr.array[i * 3] += driftSpeeds[i * 3];
      posAttr.array[i * 3 + 1] += driftSpeeds[i * 3 + 1];
      posAttr.array[i * 3 + 2] += driftSpeeds[i * 3 + 2];

      if (posAttr.array[i * 3 + 1] > SPACE.HEIGHT * 0.6)
        posAttr.array[i * 3 + 1] = -SPACE.HEIGHT * 0.6;
      if (posAttr.array[i * 3 + 1] < -SPACE.HEIGHT * 0.6)
        posAttr.array[i * 3 + 1] = SPACE.HEIGHT * 0.6;
    }
    posAttr.needsUpdate = true;
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
        <bufferAttribute attach="attributes-color" count={count} array={colors} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial
        size={0.012}
        vertexColors
        transparent
        opacity={0.25}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  );
}

// ── Subtle vertical axis (barely-there center reference) ──
function AxisLine() {
  const pts = useMemo(() => [
    [0, -SPACE.HEIGHT * 0.5, 0],
    [0, SPACE.HEIGHT * 0.5, 0],
  ], []);

  return (
    <Line
      points={pts}
      color="#334466"
      lineWidth={0.5}
      transparent
      opacity={0.06}
      depthWrite={false}
    />
  );
}

// ── Time rings (horizontal planes you look through from inside) ──
function TimeRings({ timeRange }) {
  if (!timeRange) return null;
  const { min, max } = timeRange;
  const span = max - min;
  if (span <= 0) return null;

  const rings = [];
  const steps = 5;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const y = (t - 0.5) * SPACE.HEIGHT;
    const date = new Date(min + span * t);
    const label = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    rings.push({ y, label });
  }

  return (
    <group>
      {rings.map((ring, i) => (
        <group key={i}>
          <mesh position={[0, ring.y, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <ringGeometry args={[SPACE.OUTER_RADIUS - 0.02, SPACE.OUTER_RADIUS + 0.02, 80]} />
            <meshBasicMaterial
              color="#334466"
              transparent
              opacity={0.04}
              side={THREE.DoubleSide}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>
          <mesh position={[0, ring.y, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <ringGeometry args={[SPACE.INNER_RADIUS - 0.01, SPACE.INNER_RADIUS + 0.01, 60]} />
            <meshBasicMaterial
              color="#334466"
              transparent
              opacity={0.025}
              side={THREE.DoubleSide}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>
          <Html
            position={[SPACE.OUTER_RADIUS + 0.3, ring.y, 0]}
            center
            distanceFactor={30}
            style={{ pointerEvents: 'none' }}
          >
            <div className="mc-time-marker">{ring.label}</div>
          </Html>
        </group>
      ))}
    </group>
  );
}

// ── Connection threads ──
function MemoryConnections({ connections }) {
  if (!connections.length) return null;
  return (
    <group>
      {connections.map((c, i) => (
        <Line
          key={i}
          points={[c.from, c.to]}
          color={c.color}
          lineWidth={0.3}
          transparent
          opacity={0.02}
          depthWrite={false}
        />
      ))}
    </group>
  );
}

// ── Memory Orb (small, translucent, see-through) ──
function MemoryOrb({ mem, isSelected, isHighlighted, isSpeaking, onSelect }) {
  const meshRef = useRef();
  const glowRef = useRef();
  const [hovered, setHovered] = useState(false);

  const color = new THREE.Color(mem.color);
  const emissiveIntensity = isSelected ? 2 : isHighlighted ? 1.5 : hovered ? 1 : 0.5;
  const scale = isSelected ? 1.5 : isHighlighted && isSpeaking ? 1.3 : hovered ? 1.2 : 1;

  useFrame((state) => {
    if (!meshRef.current) return;
    const t = state.clock.elapsedTime;
    const h = hashStr(mem.id);
    const offset = (h % 1000) / 1000;

    // Very gentle float
    meshRef.current.position.x = mem.position[0] + Math.sin(t * 0.15 + offset * 6.28) * 0.015;
    meshRef.current.position.y = mem.position[1] + Math.sin(t * 0.2 + offset * 6.28) * 0.01;
    meshRef.current.position.z = mem.position[2] + Math.cos(t * 0.15 + offset * 6.28) * 0.015;

    const target = scale * mem.size * 3;
    meshRef.current.scale.lerp(new THREE.Vector3(target, target, target), 0.1);

    if (isHighlighted && isSpeaking) {
      meshRef.current.scale.multiplyScalar(1 + Math.sin(t * 4 + offset * 6) * 0.08);
    }

    if (glowRef.current) {
      glowRef.current.position.copy(meshRef.current.position);
      const g = target * 1.5;
      glowRef.current.scale.set(g, g, g);
    }
  });

  const onOver = useCallback(e => {
    e.stopPropagation();
    setHovered(true);
    document.body.style.cursor = 'pointer';
  }, []);

  const onOut = useCallback(() => {
    setHovered(false);
    document.body.style.cursor = 'auto';
  }, []);

  const onClick = useCallback(e => {
    e.stopPropagation();
    onSelect && onSelect(mem);
  }, [mem, onSelect]);

  return (
    <>
      {/* Soft glow halo */}
      <mesh ref={glowRef} position={mem.position}>
        <sphereGeometry args={[1, 8, 8]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={isSelected ? 0.08 : isHighlighted ? 0.05 : hovered ? 0.04 : 0.015}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </mesh>

      {/* Core orb — translucent */}
      <mesh
        ref={meshRef}
        position={mem.position}
        onPointerOver={onOver}
        onPointerOut={onOut}
        onClick={onClick}
      >
        <sphereGeometry args={[1, 12, 12]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={emissiveIntensity}
          roughness={0.3}
          metalness={0.05}
          transparent
          opacity={0.4}
        />
      </mesh>

      {/* Tooltip */}
      {hovered && (
        <Html position={mem.position} center distanceFactor={18} style={{ pointerEvents: 'none' }}>
          <div className="mc-tooltip">
            <span className="mc-tooltip-cat" style={{ color: mem.color }}>{mem.cat}</span>
            <p className="mc-tooltip-text">
              {mem.text.substring(0, 80)}{mem.text.length > 80 ? '…' : ''}
            </p>
            <span className="mc-tooltip-date">
              {new Date(mem.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
        </Html>
      )}
    </>
  );
}

// ── Main Scene (you're inside the space, looking around) ──
function SpaceScene({ positioned, connections, timeRange, selectedMemory, highlightedMemoryIds, isSpeaking, onMemoryClick }) {
  const [autoRotate, setAutoRotate] = useState(true);
  const highlightedIds = useMemo(() => new Set((highlightedMemoryIds || []).map(m => m.id)), [highlightedMemoryIds]);

  return (
    <>
      {/* Minimal ambient lighting */}
      <ambientLight intensity={0.06} />
      <pointLight position={[0, SPACE.HEIGHT * 0.4, 0]} intensity={0.2} color="#6688bb" distance={22} />
      <pointLight position={[0, -SPACE.HEIGHT * 0.4, 0]} intensity={0.12} color="#7766aa" distance={22} />

      {/* Background stars + depth fog */}
      <Stars radius={120} depth={80} count={1500} factor={3} saturation={0.08} fade speed={0.15} />
      <fog attach="fog" args={['#030308', 4, 22]} />

      {/* Camera — start inside, free movement */}
      <OrbitControls
        enableDamping
        dampingFactor={0.06}
        minDistance={0.5}
        maxDistance={30}
        autoRotate={autoRotate}
        autoRotateSpeed={0.12}
        onStart={() => setAutoRotate(false)}
        onEnd={() => setTimeout(() => setAutoRotate(true), 6000)}
      />

      {/* Subtle axis */}
      <AxisLine />

      {/* Ambient dust */}
      <DustParticles />

      {/* Time rings */}
      <TimeRings timeRange={timeRange} />

      {/* Connection threads */}
      <MemoryConnections connections={connections} />

      {/* Memory orbs */}
      {positioned.map(mem => (
        <MemoryOrb
          key={mem.id}
          mem={mem}
          isSelected={selectedMemory && selectedMemory.id === mem.id}
          isHighlighted={highlightedIds.has(mem.id)}
          isSpeaking={isSpeaking}
          onSelect={onMemoryClick}
        />
      ))}
    </>
  );
}

// ── Exported Component ──
export default function MemoryConstellation({ memories, onMemoryClick, selectedMemory, highlightedMemoryIds = [], isSpeaking = false }) {
  const { positioned, categoryStats, connections, timeRange } = useMemo(
    () => computeSpacePositions(memories),
    [memories]
  );

  if (!memories || memories.length === 0) {
    return (
      <div className="mc-empty">
        <div className="mc-empty-orb" />
        <p>Your memory space will appear here</p>
        <p className="mc-empty-sub">Start conversations to populate your memory space</p>
      </div>
    );
  }

  return (
    <div className="mc-root">
      <Canvas
        camera={{ position: [0, 0.5, 0.1], fov: 65 }}
        gl={{ antialias: true, alpha: true }}
        dpr={[1, 1.5]}
        style={{ background: 'radial-gradient(ellipse at center, #080812 0%, #030308 100%)' }}
      >
        <SpaceScene
          positioned={positioned}
          connections={connections}
          timeRange={timeRange}
          selectedMemory={selectedMemory}
          highlightedMemoryIds={highlightedMemoryIds}
          isSpeaking={isSpeaking}
          onMemoryClick={onMemoryClick}
        />
      </Canvas>

      {/* Stats */}
      <div className="mc-stats">
        <span>{memories.length} memories</span>
        <span className="mc-stats-sep">·</span>
        <span>{categoryStats.length} streams</span>
        <span className="mc-stats-sep">·</span>
        <span>{connections.length} threads</span>
      </div>

      {/* Instructions */}
      <div className="mc-hint">
        Drag to look around · Scroll to move · Click a memory to explore
      </div>
    </div>
  );
}
